import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ServicesPage = () => {
  const [timeLeft, setTimeLeft] = useState(20 * 60); // 20 minutes in seconds

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const benefits = [
    {
      icon: 'ri-search-eye-line',
      title: 'Identify your biggest hidden triggers',
      description: 'Emotional, situational, and mental patterns that quietly pull you back into addiction.',
    },
    {
      icon: 'ri-shield-cross-line',
      title: 'Understand why willpower keeps failing you',
      description: 'And what\'s really happening in the moments before relapse.',
    },
    {
      icon: 'ri-eye-line',
      title: 'See the exact habits and environments keeping you stuck',
      description: 'So you know what needs to change (and what doesn\'t).',
    },
    {
      icon: 'ri-compass-line',
      title: 'Get a clear starting point for real change',
      description: 'Not guesses, not guilt, but insight you can act on immediately.',
    },
  ];

  const modules = [
    {
      number: '01',
      title: 'Understanding Your Addiction',
      description: 'Identify the root causes, triggers, and patterns that have kept you trapped in the cycle.',
      icon: 'ri-brain-line',
      color: 'from-teal-500 to-emerald-500',
    },
    {
      number: '02',
      title: 'Building Daily Habits',
      description: 'Create sustainable routines and practices that support your recovery and strengthen your resolve.',
      icon: 'ri-calendar-check-line',
      color: 'from-emerald-500 to-green-500',
    },
    {
      number: '03',
      title: 'Emotional Mastery',
      description: 'Learn to process, sit with, and manage the emotions that lead you back to addiction.',
      icon: 'ri-heart-pulse-line',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      number: '04',
      title: 'Environment Design',
      description: 'Structure your physical and digital spaces to eliminate temptation and support sobriety.',
      icon: 'ri-home-heart-line',
      color: 'from-purple-500 to-indigo-500',
    },
    {
      number: '05',
      title: 'Mindset Transformation',
      description: 'Break free from limiting beliefs and develop thinking patterns that support lasting change.',
      icon: 'ri-lightbulb-line',
      color: 'from-amber-500 to-orange-500',
    },
    {
      number: '06',
      title: 'Spiritual Reconnection',
      description: 'Rebuild your relationship with Allah and find peace in your worship and daily life.',
      icon: 'ri-moon-line',
      color: 'from-rose-500 to-pink-500',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section with Compelling Question */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)' }}></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center text-white relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold mb-8 leading-tight">
            How much longer can you live with the gap between who people think you are — and who you really are in private?
          </h1>
        </div>
      </section>

      {/* Have You Ever Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-16 text-center">
            Have You Ever…
          </h2>

          <div className="space-y-12">
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 flex items-center justify-center bg-rose-100 rounded-full flex-shrink-0 mt-1">
                <i className="ri-loop-left-line text-rose-600 text-xl"></i>
              </div>
              <p className="text-xl text-gray-700 leading-relaxed">
                Made sincere tawbah, felt relief for a moment — then returned to the same sin and hated yourself for it?
              </p>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                <i className="ri-user-forbid-line text-amber-600 text-xl"></i>
              </div>
              <p className="text-xl text-gray-700 leading-relaxed">
                Prayed while carrying a secret you're terrified of being exposed — feeling like a hypocrite standing before Allah?
              </p>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0 mt-1">
                <i className="ri-time-line text-purple-600 text-xl"></i>
              </div>
              <p className="text-xl text-gray-700 leading-relaxed">
                Wasted entire days to addiction, then lay awake at night crushed by guilt, knowing your time and barakah are slipping away?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Timer Section */}
      <section className="py-16 bg-gradient-to-br from-rose-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 text-rose-700 rounded-full text-sm font-bold mb-4">
                <i className="ri-time-line text-xl"></i>
                <span>LIMITED TIME BONUS</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Book Within the Next 20 Minutes
              </h3>
              <p className="text-xl text-gray-700 mb-6">
                Get the full £97.00 assessment cost credited towards your 1:1 coaching program
              </p>
            </div>

            {/* Countdown Display */}
            <div className="flex justify-center gap-4 mb-8">
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 min-w-[120px] shadow-lg">
                <div className="text-5xl font-bold text-white text-center mb-2">
                  {String(minutes).padStart(2, '0')}
                </div>
                <div className="text-teal-100 text-sm font-semibold text-center uppercase tracking-wide">
                  Minutes
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-4xl font-bold text-gray-400">:</span>
              </div>
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 min-w-[120px] shadow-lg">
                <div className="text-5xl font-bold text-white text-center mb-2">
                  {String(seconds).padStart(2, '0')}
                </div>
                <div className="text-teal-100 text-sm font-semibold text-center uppercase tracking-wide">
                  Seconds
                </div>
              </div>
            </div>

            {timeLeft > 0 ? (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 mb-8 border-2 border-emerald-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-xl flex-shrink-0">
                    <i className="ri-gift-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Here's What This Means For You:
                    </h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-emerald-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span>Take the Unveil Your Triggers Assessment for £97.00</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-emerald-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span>If you decide to join the 1:1 coaching program, the full £97.00 is credited to your tuition</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-emerald-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span>Essentially, you get the assessment completely free when you commit to your transformation</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-6 mb-8 border-2 border-gray-200">
                <div className="text-center">
                  <i className="ri-time-line text-4xl text-gray-400 mb-3"></i>
                  <p className="text-xl font-semibold text-gray-700">
                    Time's up! But the assessment is still available for £97.00
                  </p>
                </div>
              </div>
            )}

            <div className="text-center">
              <a
                href="https://calendly.com/coachabdurrahman1/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-lg font-bold rounded-full hover:from-teal-700 hover:to-emerald-700 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
              >
                {timeLeft > 0 ? '🎁 Claim Your Bonus Now' : '👉 Book Your Assessment'}
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              {timeLeft > 0 && (
                <p className="text-sm text-gray-600 mt-4">
                  ⏰ Bonus expires in {minutes}:{String(seconds).padStart(2, '0')}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* If This Is You Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-8">
              If This Is You… Take the Unveil Your Triggers Assessment
            </h2>
            <div className="space-y-4 text-2xl text-gray-700 max-w-3xl mx-auto">
              <p className="font-semibold">You don't need more motivation.</p>
              <p className="font-semibold">You need clarity.</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-10 md:p-12 shadow-xl mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-12 text-center">
              The Unveil Your Triggers Assessment helps you finally understand why you keep falling back into addiction — and what's actually driving it beneath the surface.
            </p>

            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              When you complete the assessment, you'll finally:
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex-shrink-0">
                    <i className={`${benefit.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {benefit.title}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mb-12">
            <p className="text-2xl text-gray-700 mb-2">
              This isn't about labels or judgment. It's about exposure before freedom.
            </p>
            <p className="text-2xl font-bold text-gray-900">
              Take the first step towards breaking the chains of addiction — grab your spot for £97.00
            </p>
          </div>

          <div className="text-center">
            <a
              href="https://calendly.com/coachabdurrahman1/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-lg font-bold rounded-full hover:from-teal-700 hover:to-emerald-700 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
            >
              👉 Take the Unveil Your Triggers Assessment Now
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <p className="text-lg text-gray-700 mt-8 max-w-2xl mx-auto leading-relaxed">
              And finally understand what's been controlling you — so you can stop letting it win
            </p>
          </div>
        </div>
      </section>

      {/* Transition to 1:1 Coaching Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-8 leading-tight">
              What Happens After the Assessment?
            </h2>
          </div>

          <div className="space-y-8 text-center max-w-3xl mx-auto mb-12">
            <p className="text-2xl font-semibold text-gray-900">
              The Unveil Your Triggers Assessment gives you awareness.
            </p>
            <p className="text-2xl font-semibold text-gray-900">
              But awareness alone doesn't create change.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              Once you see your triggers clearly, the next step is knowing what to do with them — day by day, moment by moment, when real life hits.
            </p>
            <p className="text-2xl font-bold text-teal-700">
              That's where 1:1 coaching comes in.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-10 md:p-12 border-2 border-teal-200 mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              In private coaching, we take what the assessment reveals and:
            </h3>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <p className="text-xl text-gray-700 leading-relaxed pt-2">
                  turn insight into a clear, personal plan,
                </p>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex-shrink-0">
                  <i className="ri-tools-line text-2xl text-white"></i>
                </div>
                <p className="text-xl text-gray-700 leading-relaxed pt-2">
                  build habits and emotional tools around your specific triggers,
                </p>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <p className="text-xl text-gray-700 leading-relaxed pt-2">
                  and create structure so you're no longer reacting — you're responding with control.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <p className="text-2xl font-semibold text-gray-900 mb-4">
              You don't need more information.
            </p>
            <p className="text-2xl font-semibold text-gray-900 mb-8">
              You need implementation, accountability, and guidance.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
              If you're ready to stop managing addiction and start leaving it behind,<br />
              1:1 coaching is where that shift actually happens.
            </p>
          </div>

          <div className="text-center">
            <a
              href="https://calendly.com/coachabdurrahman1/application-for-1-2-1-coaching"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-lg font-bold rounded-full hover:from-teal-700 hover:to-emerald-700 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
            >
              👉 Apply for 1:1 Coaching
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <p className="text-lg text-gray-700 mt-8 max-w-2xl mx-auto leading-relaxed">
              Let's turn clarity into consistent change.
            </p>
          </div>
        </div>
      </section>

      {/* Program Outline Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
              The Freedom From Addiction 1:1 Coaching Program
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive 6-module journey designed to help you break free from addiction and rebuild your life
            </p>
          </div>

          {/* Modules Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-brain-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Module 1: Understanding Your Addiction</h3>
              <p className="text-gray-700 leading-relaxed">
                Identify root causes, triggers, and patterns. Gain clarity on why you struggle and what keeps you stuck.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-calendar-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Module 2: Building Daily Habits</h3>
              <p className="text-gray-700 leading-relaxed">
                Create sustainable routines that support recovery. Replace destructive patterns with empowering actions.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Module 3: Emotional Mastery</h3>
              <p className="text-gray-700 leading-relaxed">
                Learn to process emotions without numbing. Develop healthy coping mechanisms for stress and pain.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Module 4: Environment Design</h3>
              <p className="text-gray-700 leading-relaxed">
                Structure your physical and social environment to support sobriety. Remove temptations and build support systems.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-red-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-lightbulb-flash-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Module 5: Mindset Transformation</h3>
              <p className="text-gray-700 leading-relaxed">
                Shift from victim to victor. Develop unshakeable belief in your ability to stay clean and build a new identity.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-moon-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Module 6: Spiritual Reconnection</h3>
              <p className="text-gray-700 leading-relaxed">
                Rebuild your relationship with Allah. Find purpose, peace, and strength through Islamic principles and practices.
              </p>
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-gradient-to-br from-gray-900 to-teal-900 rounded-3xl p-12 mb-20 text-white">
            <h3 className="text-4xl font-bold mb-12 text-center">What's Included</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-heart-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Personalized 1:1 Sessions</h4>
                  <p className="text-teal-100">Weekly coaching tailored to your specific situation</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-message-3-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Direct Access Support</h4>
                  <p className="text-teal-100">Message support between sessions when you need it</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Custom Action Plans</h4>
                  <p className="text-teal-100">Step-by-step strategies designed for your triggers</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Complete Confidentiality</h4>
                  <p className="text-teal-100">Safe, judgment-free space for honest conversation</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-moon-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Islamic Framework</h4>
                  <p className="text-teal-100">Guidance rooted in faith and Islamic principles</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-line-chart-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Accountability & Tracking</h4>
                  <p className="text-teal-100">Monitor progress and celebrate milestones together</p>
                </div>
              </div>
            </div>
          </div>
          {/* Journey Visual */}
          <div className="mb-20">
            <h3 className="text-4xl font-bold text-center text-gray-900 mb-12">Your Journey to Freedom</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">1</span>
                </div>
                <h4 className="font-bold text-xl mb-2">Assessment</h4>
                <p className="text-gray-600">Unveil your triggers and patterns</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">2</span>
                </div>
                <h4 className="font-bold text-xl mb-2">Strategy</h4>
                <p className="text-gray-600">Build your personalized plan</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">3</span>
                </div>
                <h4 className="font-bold text-xl mb-2">Action</h4>
                <p className="text-gray-600">Implement with support and accountability</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">4</span>
                </div>
                <h4 className="font-bold text-xl mb-2">Freedom</h4>
                <p className="text-gray-600">Live clean, confident, and in control</p>
              </div>
            </div>
          </div>

          {/* Real Testimonial */}
          <div className="mb-20">
            <h3 className="text-4xl font-bold text-center text-gray-900 mb-12">Real Results</h3>
            <div className="max-w-4xl mx-auto">
              <img 
                src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/9283fbe299b272da57d11e1aa4fa7198.png" 
                alt="Client testimonial" 
                className="w-full rounded-3xl shadow-xl"
              />
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-teal-600 to-emerald-600 rounded-3xl p-12 text-white">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Apply for 1:1 Coaching Now
            </h3>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Stop managing addiction. Start leaving it behind. This is where clarity becomes consistent change.
            </p>
            <a
              href="https://calendly.com/coachabdurrahman1/application-for-1-2-1-coaching"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-12 py-5 bg-white text-teal-600 text-lg font-bold rounded-full hover:bg-teal-50 transition-all shadow-xl cursor-pointer whitespace-nowrap"
            >
              Book Your Application Call
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <p className="mt-6 text-teal-100 text-sm">
              Limited spots available • 100% confidential • Islamic framework
            </p>
          </div>
        </div>
      </section>

      {/* Fast Action Discount Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <i className="ri-flashlight-line text-4xl text-white"></i>
                <h3 className="text-3xl md:text-4xl font-bold text-white">
                  Fast Action Bonus
                </h3>
                <i className="ri-flashlight-line text-4xl text-white"></i>
              </div>
              <p className="text-xl text-amber-50 font-semibold">
                For Serious Men Ready to Commit
              </p>
            </div>

            <div className="p-10 md:p-12">
              <div className="text-center mb-10">
                <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Pay in Full & Save
                </h4>
                <p className="text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
                  When you commit to your transformation and pay in full for the complete 1:1 coaching program, you'll receive an exclusive discount as a reward for your decisive action.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-10">
                <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8 border-2 border-gray-200">
                  <div className="text-center">
                    <div className="text-gray-500 text-lg mb-2">Standard Payment Plan</div>
                    <div className="text-4xl font-bold text-gray-900 mb-4">Monthly Payments</div>
                    <p className="text-gray-600">
                      Flexible payment options available
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-8 border-4 border-amber-400 relative overflow-hidden shadow-xl">
                  <div className="absolute top-4 right-4 bg-white text-amber-600 px-4 py-2 rounded-full text-sm font-bold">
                    BEST VALUE
                  </div>
                  <div className="text-center">
                    <div className="text-amber-100 text-lg mb-2">Fast Action Discount</div>
                    <div className="text-4xl font-bold text-white mb-2">Pay in Full</div>
                    <div className="text-2xl font-bold text-white mb-4">Save £500</div>
                    <p className="text-amber-50">
                      One-time payment for the complete program
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 mb-10 border-2 border-teal-200">
                <h5 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Why This Discount Exists
                </h5>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                      <i className="ri-shield-check-line text-xl text-white"></i>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed pt-1">
                      <strong>You're showing commitment.</strong> Paying in full demonstrates you're serious about change — and that level of commitment accelerates results.
                    </p>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                      <i className="ri-focus-3-line text-xl text-white"></i>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed pt-1">
                      <strong>You remove financial friction.</strong> No monthly decisions, no payment reminders — just full focus on your recovery journey.
                    </p>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                      <i className="ri-hand-heart-line text-xl text-white"></i>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed pt-1">
                      <strong>You invest in yourself fully.</strong> This discount rewards men who are ready to go all-in on their transformation without hesitation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-xl font-semibold text-gray-900 mb-6">
                  Ready to commit and save £500?
                </p>
                <a
                  href="https://calendly.com/coachabdurrahman1/application-for-1-2-1-coaching"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-bold rounded-full hover:from-amber-600 hover:to-orange-600 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
                >
                  Apply Now & Claim Your Discount
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <p className="text-sm text-gray-600 mt-6 max-w-2xl mx-auto">
                  Discount automatically applied when you choose the pay-in-full option during your application call. Limited to serious applicants only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
